import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { PaymentGatewayProviderActions } from '../code/payment-gateways';
import { PaymentGatewayComponent } from '../components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { catchError, map } from 'rxjs/operators';
import {
  BlackbaudCreateRevenuePaymentRequest,
  BlackbaudCreateRevenuePaymentResponse,
  BlackbaudFetchTokenRequest,
  BlackbaudFetchTokenResponse,
  BlackbaudPublicKeyResponse,
  BlackbaudStoreTokenRequest,
  BlackbaudStoreTokenResponse,
  EwayCustomerResponse,
  EwayDirectPaymentResponse,
  EwayOnceOffRequest,
  EwayStoreTokenRequest,
  EwayUpdateTokenRequest,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class PaymentGatewayService {
  private readonly paymentGatewayApi = `${environment.apiPath}/payment-gateways`;

  constructor(private modalSvc: NgbModal, private http: HttpClient) {}

  blackbaudCreateRevenuePayment(request: BlackbaudCreateRevenuePaymentRequest) {
    return this.http
      .post<BlackbaudCreateRevenuePaymentResponse>(
        `${this.paymentGatewayApi}/blackbaud/create-revenue`,
        request
      )
      .pipe(
        map((vm) => ({
          result: 'success',
          request: {
            ...request,
            number: `${'*'.repeat(
              request.number.length - 4
            )}${request.number.slice(-4)}`,
          } as BlackbaudCreateRevenuePaymentRequest,
          response: vm,
        })),
        catchError((err) =>
          of({ result: 'error', error: err?.message as string })
        )
      );
  }

  blackbaudFetchToken(request: BlackbaudFetchTokenRequest) {
    return this.http.get<BlackbaudFetchTokenResponse>(
      `${this.paymentGatewayApi}/blackbaud/token`,
      { params: request }
    );
  }

  blackbaudPublicKey(campaignId: number) {
    return this.http.get<BlackbaudPublicKeyResponse>(
      `${this.paymentGatewayApi}/blackbaud/publicKey`,
      { params: { campaignId } }
    );
  }

  blackbaudStoreToken(request: BlackbaudStoreTokenRequest) {
    return this.http
      .post<BlackbaudStoreTokenResponse>(
        `${this.paymentGatewayApi}/blackbaud/token`,
        request
      )
      .pipe(
        map((vm) => ({
          result: 'success',
          request: {
            ...request,
            number: `${'*'.repeat(
              request.number.length - 4
            )}${request.number.slice(-4)}`,
          } as BlackbaudStoreTokenRequest,
          response: vm,
        })),
        catchError((err) =>
          of({ result: 'error', error: err?.message as string })
        )
      );
  }

  ewayOnceOff(request: EwayOnceOffRequest) {
    return this.http
      .post<EwayDirectPaymentResponse>(
        `${this.paymentGatewayApi}/eway/onceoff`,
        request
      )
      .pipe(
        map((vm) => ({
          result: 'success',
          request: {
            ...request,
            number: `${'*'.repeat(
              request.number.length - 4
            )}${request.number.slice(-4)}`,
          } as EwayOnceOffRequest,
          response: vm,
        })),
        catchError((err) =>
          of({ result: 'error', error: err?.message as string })
        )
      );
  }

  ewayStoreToken(request: EwayStoreTokenRequest) {
    return this.http
      .post<EwayCustomerResponse>(
        `${this.paymentGatewayApi}/eway/token`,
        request
      )
      .pipe(
        map((vm) => ({
          result: 'success',
          request: {
            ...request,
            number: `${'*'.repeat(
              request.number.length - 4
            )}${request.number.slice(-4)}`,
          } as EwayStoreTokenRequest,
          response: vm,
        })),
        catchError((err) =>
          of({ result: 'error', error: err?.message as string })
        )
      );
  }

  ewayUpdateToken(request: EwayUpdateTokenRequest) {
    return this.http
      .put<EwayCustomerResponse>(
        `${this.paymentGatewayApi}/eway/token`,
        request
      )
      .pipe(
        map((vm) => ({
          result: 'success',
          request: {
            ...request,
            number: `${'*'.repeat(
              request.number.length - 4
            )}${request.number.slice(-4)}`,
          } as EwayUpdateTokenRequest,
          response: vm,
        })),
        catchError((err) =>
          of({ result: 'error', error: err?.message as string })
        )
      );
  }

  showModal$(
    providerAction: PaymentGatewayProviderActions,
    campaignId: number | undefined,
    additionalValues?: { [key: string]: string | number | undefined }
  ): Observable<string> {
    const modalRef = this.modalSvc.open(PaymentGatewayComponent, {
      size:
        providerAction.provider == 'blackbaud' &&
        providerAction.action == 'update-token'
          ? 'xl'
          : 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.campaignId = campaignId;
    modalRef.componentInstance.providerAction = providerAction;
    modalRef.componentInstance.additionalValues = additionalValues;
    return from(modalRef.result).pipe(map((result) => JSON.stringify(result)));
  }
}
